import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Megaphone, Handshake, Code, ChevronRight, ChevronLeft, Loader2, Sparkles, Check, Clock, BookOpen, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { trackPlanGenerated } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = { Briefcase, Megaphone, Handshake, Code };
const STEP_LABELS = ["Role", "Technologies", "Outcome", "Preferences", "Generate"];
const EXEC_STEP_LABELS = ["Role", "Goal", "Generate"];

// ── Solution Area taxonomy for technology grouping ──────────────
const SOLUTION_AREA_TABS = [
  { id: "cloud_ai", label: "Cloud & AI Platform" },
  { id: "security", label: "Security" },
  { id: "ai_workforce", label: "AI Workforce" },
  { id: "biz_process", label: "AI Business Processes" },
  { id: "all", label: "All Technologies" },
];

const TECH_TO_SOLUTION_AREA = {
  microsoft_foundry: "cloud_ai", azure_openai: "cloud_ai", microsoft_fabric: "cloud_ai",
  azure_infrastructure: "cloud_ai", azure_databricks: "cloud_ai", azure_databases: "cloud_ai",
  sap_on_azure: "cloud_ai", azure_arc: "cloud_ai", azure_ml: "cloud_ai",
  power_bi: "cloud_ai", github_copilot: "cloud_ai",
  security_copilot: "security", defender_xdr: "security", sentinel: "security",
  microsoft_purview: "security", microsoft_entra: "security", defender_for_cloud: "security",
  microsoft_intune: "security", defender_for_endpoint: "security",
  microsoft_365_copilot: "ai_workforce", copilot_studio: "ai_workforce",
  microsoft_viva: "ai_workforce", microsoft_teams: "ai_workforce",
  windows_365: "ai_workforce", microsoft_365_admin: "ai_workforce",
  dynamics_365_copilot: "biz_process", power_platform: "biz_process",
  dynamics_365_bc: "biz_process", dynamics_365_contact_center: "biz_process",
  dynamics_365_scm: "biz_process", dynamics_365_sales: "biz_process",
  dynamics_365_cs: "biz_process", power_apps: "biz_process", power_automate: "biz_process",
};

const AREA_COLORS = {
  cloud_ai: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-400" },
  security: { bg: "bg-[#194449]/10", border: "border-[#194449]/30", text: "text-[#194449]", dot: "bg-[#194449]" },
  ai_workforce: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", dot: "bg-green-400" },
  biz_process: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", dot: "bg-purple-400" },
};

function ProgressBar({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center gap-1.5 w-full max-w-md mx-auto" data-testid="progress-bar">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div key={i} className="flex-1">
          <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${i < currentStep ? "bg-[#07641D]" : i === currentStep ? "bg-[#194449]" : "bg-[#E2E8F0]"}`} />
        </div>
      ))}
    </div>
  );
}

function StepWrapper({ children, stepKey }) {
  return (
    <motion.div key={stepKey} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} className="w-full">
      {children}
    </motion.div>
  );
}

// Step 1: Role Bucket
function RoleBucketStep({ roleBuckets, selected, onSelect }) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          What best describes you?
        </h2>
        <p className="text-[#64748B] mt-2 text-sm">Select your primary role to personalize your learning path.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {roleBuckets.map((role) => {
          const IconComp = ROLE_ICONS[role.icon] || Briefcase;
          const isSelected = selected === role.id;
          return (
            <button key={role.id} onClick={() => onSelect(role.id)}
              className={`group relative p-5 rounded-xl border-2 text-left transition-all duration-200 ${isSelected ? "border-[#07641D] bg-[#07641D]/5 shadow-md" : "border-[#E2E8F0] bg-white hover:border-[#194449]/40 hover:shadow-sm"}`}
              data-testid={`role-bucket-${role.id}`}>
              <div className="flex items-start gap-3.5">
                <div className={`p-2.5 rounded-lg shrink-0 ${isSelected ? "bg-[#07641D]/10 text-[#07641D]" : "bg-[#F4F3F5] text-[#64748B] group-hover:text-[#194449]"}`}>
                  <IconComp className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#091F2C] text-sm">{role.label}</div>
                  <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{role.description}</p>
                </div>
                {isSelected && <div className="absolute top-3 right-3"><Check className="w-4 h-4 text-[#07641D]" /></div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Step 2: Technology Selection with Solution Area tabs
function TechnologyStep({ technologies, selected, onToggle, coverage }) {
  // Default to first available category tab (not "All") so user lands in a curated view
  const firstCategoryTab = SOLUTION_AREA_TABS.find(
    (tab) => tab.id !== "all" && technologies.some((t) => TECH_TO_SOLUTION_AREA[t.id] === tab.id)
  );
  const [activeArea, setActiveArea] = useState(firstCategoryTab?.id || "all");

  // Show category tabs that have at least 1 tech for this role; "All Technologies" always appears last
  const availableTabs = [
    ...SOLUTION_AREA_TABS.filter(
      (tab) => tab.id !== "all" && technologies.some((t) => TECH_TO_SOLUTION_AREA[t.id] === tab.id)
    ),
    SOLUTION_AREA_TABS.find((t) => t.id === "all"),
  ];

  const filteredTechs =
    activeArea === "all"
      ? technologies
      : technologies.filter((t) => TECH_TO_SOLUTION_AREA[t.id] === activeArea);

  // Count selections per area for tab badges
  const countByArea = {};
  for (const tab of SOLUTION_AREA_TABS) {
    countByArea[tab.id] = selected.filter(
      (id) => tab.id === "all" ? true : TECH_TO_SOLUTION_AREA[id] === tab.id
    ).length;
  }

  return (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Which technologies are you focusing on?
        </h2>
        <p className="text-[#64748B] mt-2 text-sm">Select a solution area below to explore relevant technologies, then choose the ones you're focusing on.</p>
      </div>

      {/* Solution Area Filter Tabs */}
      <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto">
        {availableTabs.map((tab) => {
          const isActive = activeArea === tab.id;
          const count = countByArea[tab.id] || 0;
          const color = AREA_COLORS[tab.id];
          return (
            <button
              key={tab.id}
              onClick={() => setActiveArea(tab.id)}
              data-testid={`solution-area-tab-${tab.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                isActive
                  ? "bg-[#194449] text-white shadow-sm"
                  : "bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#194449]/40 hover:text-[#091F2C]"
              }`}
            >
              {tab.id !== "all" && color && (
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-white/70" : color.dot}`} />
              )}
              {tab.label}
              {count > 0 && (
                <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${isActive ? "bg-white/20 text-white" : "bg-[#07641D] text-white"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Area label */}
      {activeArea !== "all" && (
        <p className="text-center text-xs text-[#64748B]">
          {filteredTechs.length} {SOLUTION_AREA_TABS.find(t => t.id === activeArea)?.label} technolog{filteredTechs.length === 1 ? "y" : "ies"} available for your role
        </p>
      )}
      {activeArea === "all" && (
        <p className="text-center text-xs text-[#64748B]">
          All {filteredTechs.length} technologies available for your role
        </p>
      )}

      {/* Technology chips */}
      <div className="flex flex-wrap gap-2.5 justify-center max-w-3xl mx-auto min-h-[80px]">
        <AnimatePresence>
          {filteredTechs.map((tech) => {
            const isSelected = selected.includes(tech.id);
            const area = TECH_TO_SOLUTION_AREA[tech.id];
            const color = AREA_COLORS[area];
            // Gray-out chips that have zero resources for this role
            // coverage=null means not loaded yet (no gray-out); missing key = 0 resources
            const resourceCount = coverage ? (coverage[tech.id] ?? 0) : -1;
            const hasNoResources = coverage !== null && resourceCount === 0;
            return (
              <motion.div
                key={tech.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="relative group"
                title={hasNoResources ? "No resources available for your role yet" : undefined}
                data-testid={`tech-chip-wrapper-${tech.id}`}
              >
                <button
                  onClick={() => !hasNoResources && onToggle(tech.id)}
                  disabled={hasNoResources}
                  className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 ${
                    hasNoResources
                      ? "border-[#E2E8F0] bg-[#F8F8F8] text-[#C4C4C4] cursor-not-allowed opacity-60"
                      : isSelected
                      ? "border-[#07641D] bg-[#07641D] text-white shadow-sm"
                      : color
                      ? `${color.bg} ${color.border} ${color.text} hover:shadow-sm`
                      : "border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#194449]/40"
                  }`}
                  data-testid={`tech-option-${tech.id}`}
                >
                  {isSelected && !hasNoResources && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                  {tech.label}
                  {hasNoResources && (
                    <span className="ml-1.5 text-[10px] text-[#C4C4C4]">No content</span>
                  )}
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {filteredTechs.length === 0 && (
          <p className="text-sm text-[#64748B] text-center pt-4">No technologies in this area for your role.</p>
        )}
      </div>

      {selected.length === 0 ? (
        <p className="text-center text-xs text-[#64748B] pt-2" data-testid="tech-required-hint">
          Select at least one technology to continue.
        </p>
      ) : (
        <p className="text-center text-xs text-[#64748B]">{selected.length} selected</p>
      )}
    </div>
  );
}

// Step 3: Desired Outcome
function OutcomeStep({ outcomes, selected, onSelect }) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          What do you want to achieve?
        </h2>
        <p className="text-[#64748B] mt-2 text-sm">Choose the outcome that best matches your current goal.</p>
      </div>
      <div className="flex flex-col gap-3 max-w-xl mx-auto">
        {outcomes.map((outcome) => {
          const isSelected = selected === outcome.id;
          return (
            <button key={outcome.id} onClick={() => onSelect(outcome.id)}
              className={`px-5 py-4 rounded-xl border-2 text-left text-sm font-medium transition-all duration-200 ${isSelected ? "border-[#07641D] bg-[#07641D]/5 text-[#091F2C] shadow-sm" : "border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#194449]/40 hover:text-[#091F2C]"}`}
              data-testid={`outcome-option-${outcome.id}`}>
              <div className="flex items-center justify-between">
                <span>{outcome.label}</span>
                {isSelected && <Check className="w-4 h-4 text-[#07641D] shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Step 4: Time + Learning Style
function PreferencesStep({ timeOptions, learningStyles, selectedTime, selectedStyle, additionalContext, onTimeSelect, onStyleSelect, onContextChange }) {
  return (
    <div className="space-y-8">
      <div className="text-center mb-4">
        <h2 className="text-2xl md:text-3xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          How much time and how do you prefer to learn?
        </h2>
        <p className="text-[#64748B] mt-2 text-sm">This helps us size and format your plan.</p>
      </div>
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#194449]" />
          <span className="text-sm font-medium text-[#091F2C]">Time available</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {timeOptions.map((opt) => {
            const isSelected = selectedTime === opt.id;
            return (
              <button key={opt.id} onClick={() => onTimeSelect(opt.id)}
                className={`px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${isSelected ? "border-[#07641D] bg-[#07641D]/5 text-[#091F2C]" : "border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#194449]/40"}`}
                data-testid={`time-option-${opt.id}`}>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-[#194449]" />
          <span className="text-sm font-medium text-[#091F2C]">Learning style</span>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {learningStyles.map((s) => {
            const isSelected = selectedStyle === s.id;
            return (
              <button key={s.id} onClick={() => onStyleSelect(s.id)}
                className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 ${isSelected ? "border-[#07641D] bg-[#07641D] text-white" : "border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#194449]/40"}`}
                data-testid={`style-option-${s.id}`}>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#194449]" />
          <span className="text-sm font-medium text-[#091F2C]">What specific skills or outcomes are you hoping to achieve? <span className="text-xs text-[#DC2626] font-normal">*</span></span>
        </div>
        <p className="text-xs text-[#64748B] mb-3 ml-6">Sharing detail here helps us recommend more relevant resources.</p>
        <textarea
          value={additionalContext || ""}
          onChange={(e) => onContextChange(e.target.value)}
          placeholder="Examples: 'I want to pass the SC-200 cert', 'focused on Microsoft Purview for compliance use cases', 'need hands-on labs for SOC analyst work'…"
          rows={4}
          maxLength={500}
          className="w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-sm text-[#091F2C] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#194449]/60 focus:ring-2 focus:ring-[#194449]/10 transition-all resize-none"
          data-testid="additional-context-input"
        />
        <p className="text-[10px] mt-1.5 text-right">
          <span className={(additionalContext || "").trim().length < 10 ? "text-[#DC2626]" : "text-[#64748B]"}>
            {(additionalContext || "").length}/500{(additionalContext || "").trim().length < 10 ? " · Please add at least 10 characters" : ""}
          </span>
          <span className="text-[#64748B]"> · We'll also auto-detect any products you mention.</span>
        </p>
      </div>
    </div>
  );
}

// Step 5: Summary + Generate
function GenerateStep({ config, selections, generating }) {
  const roleBucket = (config.role_buckets || []).find((r) => r.id === selections.roleBucket);
  const outcome = (config.role_outcomes?.[selections.roleBucket] || []).find((o) => o.id === selections.outcome);
  const timeOpt = (config.time_options || []).find((t) => t.id === selections.time);
  const styleOpt = (config.learning_styles || []).find((s) => s.id === selections.style);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Ready to generate your plan
        </h2>
        <p className="text-[#64748B] mt-2 text-sm">Review your selections below.</p>
      </div>
      <div className="max-w-lg mx-auto space-y-4 bg-white rounded-2xl border border-[#E2E8F0] p-6" data-testid="review-summary">
        <div>
          <span className="text-xs text-[#64748B] uppercase tracking-wider">Role</span>
          <p className="text-sm font-medium text-[#091F2C] mt-0.5">{roleBucket?.label || selections.roleBucket}</p>
        </div>
        <div className="border-t border-[#F4F3F5]" />
        <div>
          <span className="text-xs text-[#64748B] uppercase tracking-wider">Technologies</span>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {selections.technologies.length > 0
              ? selections.technologies.map((t) => <Badge key={t} variant="secondary" className="bg-[#D4EC8E] text-[#091F2C] border-0 text-xs">{t}</Badge>)
              : <span className="text-sm text-[#64748B]">General (no specific selection)</span>}
          </div>
        </div>
        <div className="border-t border-[#F4F3F5]" />
        <div>
          <span className="text-xs text-[#64748B] uppercase tracking-wider">Goal</span>
          <p className="text-sm font-medium text-[#091F2C] mt-0.5">{outcome?.label || selections.outcome}</p>
        </div>
        <div className="border-t border-[#F4F3F5]" />
        <div className="flex gap-8">
          <div>
            <span className="text-xs text-[#64748B] uppercase tracking-wider">Time</span>
            <p className="text-sm font-medium text-[#091F2C] mt-0.5">{timeOpt?.label || selections.time}</p>
          </div>
          <div>
            <span className="text-xs text-[#64748B] uppercase tracking-wider">Style</span>
            <p className="text-sm font-medium text-[#091F2C] mt-0.5">{styleOpt?.label || selections.style}</p>
          </div>
        </div>
      </div>
      {generating && (
        <div className="flex flex-col items-center gap-3 pt-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#194449]" />
          <p className="text-sm text-[#64748B]">Generating your personalized learning plan...</p>
        </div>
      )}
    </div>
  );
}

export default function IntakeFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);

  const [roleBucket, setRoleBucket] = useState("");
  const [technologies, setTechnologies] = useState([]);
  const [outcome, setOutcome] = useState("");
  const [time, setTime] = useState("");
  const [style, setStyle] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  // Coverage data: {product_id: count} for the selected role
  const [roleCoverage, setRoleCoverage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/config/assessment`).then((res) => {
      setConfig(res.data);
      setLoading(false);
    }).catch(() => {
      toast.error("Failed to load assessment config");
      setLoading(false);
    });
  }, []);

  const isExecRole = roleBucket === "executive_sponsor";

  // Progress bar derived values
  const execVisualStep = step === 0 ? 0 : step === 2 ? 1 : 2;
  const displayStep = isExecRole ? execVisualStep : step;
  const displayTotalSteps = isExecRole ? 3 : 5;
  const displayLabels = isExecRole ? EXEC_STEP_LABELS : STEP_LABELS;

  const toggleTech = useCallback((techId) => {
    setTechnologies((prev) => prev.includes(techId) ? prev.filter((t) => t !== techId) : [...prev, techId]);
  }, []);

  const canAdvance = () => {
    if (step === 0) return !!roleBucket;
    if (step === 1) return technologies.length > 0;
    if (step === 2) return !!outcome;
    if (step === 3) return !!time && !!style && additionalContext.trim().length >= 10;
    return true;
  };

  const handleNext = () => {
    if (isExecRole) {
      if (step === 0) setStep(2);
      else if (step === 2) handleGenerate();
    } else {
      if (step < 4) setStep(step + 1);
      else handleGenerate();
    }
  };

  const handleBack = () => {
    if (isExecRole) {
      if (step === 2) setStep(0);
    } else {
      if (step > 0) setStep(step - 1);
    }
  };

  const handleRoleSelect = (id) => {
    if (id !== roleBucket) {
      setTechnologies([]);
      setOutcome("");
      setRoleCoverage(null);
    }
    setRoleBucket(id);
    // Fetch technology coverage for the new role so we can gray out empty chips
    if (id && id !== "executive_sponsor") {
      axios.get(`${API}/config/technology-coverage?role_bucket=${id}`)
        .then((res) => setRoleCoverage(res.data.coverage || {}))
        .catch(() => setRoleCoverage(null)); // silent — gray-out is best-effort
    } else {
      setRoleCoverage(null);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const appToken = localStorage.getItem("app_token");
      const headers = appToken ? { Authorization: `Bearer ${appToken}` } : {};
      const res = await axios.post(`${API}/plans/generate-structured`, {
        role_bucket: roleBucket,
        selected_technologies: technologies,
        desired_outcome: outcome,
        time_available: time || "5_10h",
        learning_style: style || "mixed",
        additional_context: additionalContext || "",
        // Issue 1 (May 2026 deploy): pass the browser language so the engine
        // can suppress mismatched-language content (e.g. "- CNS" Chinese
        // cert-prep courses) for English learners.
        learner_language: (typeof navigator !== "undefined" && navigator.language) || "en",
      }, { headers });
      trackPlanGenerated(res.data.session_id, { role_bucket: roleBucket }, user?.email);
      navigate(`/plan/${res.data.session_id}`, { state: { plan: res.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Plan generation failed");
      setGenerating(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#194449]" />
      </div>
    );
  }

  const currentTechs = config.role_technologies?.[roleBucket] || [];
  const currentOutcomes = config.role_outcomes?.[roleBucket] || [];
  // Map tech IDs to objects for the TechnologyStep
  const techObjects = currentTechs.map((techId) => {
    if (typeof techId === "object") return techId;
    const heroProduct = config.hero_products?.find?.((p) => p.id === techId);
    return { id: techId, label: heroProduct?.name || heroProduct?.display_name || techId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) };
  });
  const selections = { roleBucket, technologies, outcome, time, style };

  // Determine if we're at the final action step
  const isExecOnOutcome = isExecRole && step === 2;
  const isLastNormalStep = !isExecRole && step === 4;

  return (
    <div className="min-h-screen bg-[#FFF8F3] flex flex-col">
      {/* Header */}
      <header className="w-full bg-[#194449] shadow-sm shrink-0">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/my-plans")} className="flex items-center gap-3" data-testid="logo-home-link">
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-5 brightness-0 invert" />
            <div className="w-px h-5 bg-white/30" />
            <span className="text-base font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>MyGoals</span>
          </button>
          <img src="/ms-sponsored-logo.svg" alt="Sponsored by Microsoft" className="h-7 hidden md:block" />
        </div>
      </header>

      {/* Progress */}
      <div className="w-full bg-white border-b border-[#E2E8F0] py-3 px-6">
        <div className="max-w-2xl mx-auto">
          <ProgressBar currentStep={displayStep} totalSteps={displayTotalSteps} />
          <div className="flex justify-between mt-2">
            {displayLabels.map((label, i) => (
              <span key={label} className={`text-xs font-medium transition-colors ${i === displayStep ? "text-[#194449]" : i < displayStep ? "text-[#07641D]" : "text-[#CBD5E1]"}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center pt-8 md:pt-12 pb-32 px-6">
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <StepWrapper stepKey="role">
                <RoleBucketStep roleBuckets={config.role_buckets} selected={roleBucket} onSelect={handleRoleSelect} />
              </StepWrapper>
            )}
            {step === 1 && (
              <StepWrapper stepKey="tech">
                <TechnologyStep technologies={techObjects} selected={technologies} onToggle={toggleTech} coverage={roleCoverage} />
              </StepWrapper>
            )}
            {step === 2 && (
              <StepWrapper stepKey="outcome">
                <OutcomeStep outcomes={currentOutcomes} selected={outcome} onSelect={setOutcome} />
              </StepWrapper>
            )}
            {step === 3 && (
              <StepWrapper stepKey="prefs">
                <PreferencesStep
                  timeOptions={config.time_options}
                  learningStyles={config.learning_styles}
                  selectedTime={time}
                  selectedStyle={style}
                  additionalContext={additionalContext}
                  onTimeSelect={setTime}
                  onStyleSelect={setStyle}
                  onContextChange={setAdditionalContext}
                />
              </StepWrapper>
            )}
            {step === 4 && (
              <StepWrapper stepKey="generate">
                <GenerateStep config={config} selections={selections} generating={generating} />
              </StepWrapper>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] py-4 px-6 z-40">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}
            disabled={step === 0 || generating}
            className="text-[#64748B] hover:text-[#091F2C]"
            data-testid="step-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          {(isExecOnOutcome || isLastNormalStep) ? (
            <Button onClick={handleGenerate} disabled={!canAdvance() || generating}
              className="bg-[#07641D] text-white hover:bg-[#194449] rounded-full px-8 gap-2 disabled:opacity-60"
              data-testid="generate-plan-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {isExecRole ? "Generate Results" : "Generate My Plan"}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={!canAdvance() || generating}
              className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-6 disabled:opacity-40"
              data-testid="step-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
